import {type MessageDescriptor} from '@lingui/core'
import {msg} from '@lingui/core/macro'

import {
  type ClassroomPostAudience,
  type ClassroomPostSeat,
  isTheWholeClassroom,
  mayAnyoneRead,
} from '#/state/classrooms/types'

/**
 * The translator, in either of the shapes the app reaches for one in.
 *
 * `useLingui()` has two forms in this codebase — the macro one, whose `t` is made
 * for `t`...`` template literals, and the plain one, whose `_` takes a
 * descriptor — and both are the same function by the time they run. Naming the one
 * thing this file needs from either of them keeps the callers on whichever form
 * their own file already uses, rather than making every component import a second
 * `useLingui` to talk to a helper.
 */
type Translator = (descriptor: MessageDescriptor) => string

/**
 * What one seat is called, on its own row and in the pill.
 *
 * One word per seat, and the same word wherever a seat is named, because the pill
 * and the row it opens must not call the same people two things. A capital first
 * letter is the only difference between these and the words the sentences below
 * use (`postSeatWord`): these are the names of things, those are people in the
 * middle of a sentence.
 */
export function postSeatName(_: Translator, seat: ClassroomPostSeat): string {
  switch (seat) {
    case 'teachers':
      return _(msg`Teachers`)
    case 'parents':
      return _(msg`Parents`)
    case 'students':
      return _(msg`Students`)
  }
}

/**
 * A seat said inside a sentence — *the teachers*, *the parents*.
 *
 * Kept apart from the row's own name (`postSeatName`) on purpose: a language that
 * inflects the people it names needs one form for a row and another for a
 * sentence, and a catalog can only hold both if the app asks for both. Named with
 * a comment for the translator, because *teachers* by itself is a word with five
 * possible meanings and this is the one about who may read a post.
 */
export function postSeatWord(_: Translator, seat: ClassroomPostSeat): string {
  switch (seat) {
    case 'teachers':
      return _(
        msg({
          message: 'teachers',
          comment:
            'The people who teach in a classroom, named inside a sentence about who may read a post.',
        }),
      )
    case 'parents':
      return _(
        msg({
          message: 'parents',
          comment:
            'The parents of a classroom’s students, named inside a sentence about who may read a post.',
        }),
      )
    case 'students':
      return _(
        msg({
          message: 'students',
          comment:
            'The learners of a classroom, named inside a sentence about who may read a post.',
        }),
      )
  }
}

/**
 * What an audience is called, in the reader's own language.
 *
 * Five places say it and they must not disagree: the pill in the composer that
 * carries the answer, the rows in the dialog that chose it, the sentence that says
 * what happens if the choice is not kept, the card a reader outside the audience
 * is shown instead of the post, and the room's own list. A second set of names is
 * a second thing to keep in step, which is the mistake this codebase keeps naming
 * — so the words live here, once.
 *
 * The two *everyone* answers are the two the app has a name for rather than a list
 * (`mayAnyoneRead`, `isTheWholeClassroom`): *Public* and *Only this
 * classroom* are what the author chose, and drawing them as *nobody named* or as
 * three seat names in a row would be a worse answer than the one they picked. Two
 * seats keep their own names and are joined — *Teachers and Parents* — because
 * that is exactly the audience the author built, and all three would already read
 * as *Only this classroom*.
 *
 * Taken as the translator rather than reached for through a hook, because one of
 * the callers is not a component that could hold one.
 */
export function postAudienceName(
  _: Translator,
  audience: ClassroomPostAudience,
): string {
  if (mayAnyoneRead(audience)) return _(msg`Public`)
  if (isTheWholeClassroom(audience)) return _(msg`Only this classroom`)
  if (audience.length === 1) return postSeatName(_, audience[0])
  const first = postSeatName(_, audience[0])
  const second = postSeatName(_, audience[1])
  return _(msg`${first} and ${second}`)
}
